// CurrentAgenda component
import { Calendar, Clock, MapPin, Users, RefreshCw } from 'lucide-react'
import { Event } from '../../types'
import { useTodayEvents } from '../../hooks/useEvents'
import { useAuth } from '../../hooks/useAuth'
import SafeHtmlContent from '../common/SafeHtmlContent'

interface CurrentAgendaProps {
  className?: string
  showHeader?: boolean
  compact?: boolean
}

const EVENT_TYPE_COLORS: Record<Event['eventType'], string> = {
  talk: 'bg-blue-100 text-blue-800 border-blue-200',
  session: 'bg-green-100 text-green-800 border-green-200',
  bus: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  competition: 'bg-red-100 text-red-800 border-red-200',
  other: 'bg-gray-100 text-gray-800 border-gray-200'
}

export default function CurrentAgenda({ 
  className = '', 
  showHeader = true,
  compact = false 
}: CurrentAgendaProps) {
  const { user } = useAuth()
  const { events, loading, error, refresh } = useTodayEvents()

  // Get current time for highlighting current events
  const now = new Date()
  
  // Separate current and upcoming events
  const currentEvents = events.filter(event => {
    const startTime = new Date(event.startTime)
    const endTime = new Date(event.endTime)
    return startTime <= now && endTime >= now
  })

  const upcomingEvents = events.filter(event => {
    const startTime = new Date(event.startTime)
    return startTime > now
  })

  const pastEvents = events.filter(event => {
    const endTime = new Date(event.endTime)
    return endTime < now
  })

  // Format time for display
  const formatTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  // Check if user is signed up for event
  const isSignedUp = (event: Event) => {
    return user && event.participants.includes(user.userId)
  }

  // Get time until event starts
  const getTimeUntilEvent = (startTime: string) => {
    const start = new Date(startTime)
    const diffMs = start.getTime() - now.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    
    if (diffMins < 0) return null
    if (diffMins < 60) return `${diffMins}m`
    
    const hours = Math.floor(diffMins / 60)
    const mins = diffMins % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  if (loading) {
    return (
      <div className={`${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Today's Agenda</h3>
            <div className="animate-spin">
              <RefreshCw className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        )}
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-white rounded-lg border p-4">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Today's Agenda</h3>
            <button
              onClick={refresh}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
          <button
            onClick={refresh}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className={`${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Today's Agenda</h3>
            <button
              onClick={refresh}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="text-center py-6">
          <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No events scheduled for today</p>
        </div>
      </div>
    )
  }

  const EventCard = ({ event, status }: { event: Event; status: 'current' | 'upcoming' | 'past' }) => {
    const timeUntil = status === 'upcoming' ? getTimeUntilEvent(event.startTime) : null
    const signedUp = isSignedUp(event)

    return (
      <div
        className={`bg-white rounded-lg border p-4 transition-all ${
          status === 'current' 
            ? 'border-fwo-blue bg-blue-50 shadow-md' 
            : status === 'past'
            ? 'border-gray-200 opacity-60'
            : 'border-gray-200 hover:shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Event type and status indicators */}
            <div className="flex items-center space-x-2 mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${EVENT_TYPE_COLORS[event.eventType]}`}>
                {event.eventType}
              </span>
              
              {status === 'current' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  Live
                </span>
              )}
              
              {signedUp && event.signupRequired && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                  Signed up
                </span>
              )}
            </div>

            {/* Event title */}
            <h4 className={`font-medium mb-1 ${compact ? 'text-sm' : 'text-base'} ${
              status === 'past' ? 'text-gray-500' : 'text-gray-900'
            }`}>
              {event.title}
            </h4>

            {/* Event details */}
            <div className={`flex flex-wrap items-center gap-3 ${compact ? 'text-xs' : 'text-sm'} ${
              status === 'past' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
              </div>

              {event.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}

              {event.signupRequired && (
                <div className="flex items-center space-x-1">
                  <Users className="h-3 w-3" />
                  <span>
                    {event.participants.length}
                    {event.maxParticipants && ` / ${event.maxParticipants}`}
                  </span>
                </div>
              )}
            </div>

            {/* Event description (only if not compact) */}
            {!compact && event.description && (
              <div className={`mt-2 ${
                status === 'past' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <SafeHtmlContent content={event.description} className={`text-sm ${
                  status === 'past' ? 'text-gray-400' : 'text-gray-600'
                }`} />
              </div>
            )}
          </div>

          {/* Time until event (for upcoming events) */}
          {timeUntil && status === 'upcoming' && (
            <div className="ml-3 text-right">
              <div className="text-xs text-gray-500">in</div>
              <div className="text-sm font-medium text-fwo-blue">{timeUntil}</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Today's Agenda</h3>
          <button
            onClick={refresh}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh events"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="space-y-3">
        {/* Current Events */}
        {currentEvents.map(event => (
          <EventCard key={event.eventId} event={event} status="current" />
        ))}

        {/* Upcoming Events */}
        {upcomingEvents.map(event => (
          <EventCard key={event.eventId} event={event} status="upcoming" />
        ))}

        {/* Past Events (only show if no current or upcoming events) */}
        {currentEvents.length === 0 && upcomingEvents.length === 0 && pastEvents.map(event => (
          <EventCard key={event.eventId} event={event} status="past" />
        ))}
      </div>

      {/* Summary */}
      {!compact && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {currentEvents.length > 0 && `${currentEvents.length} current`}
              {currentEvents.length > 0 && upcomingEvents.length > 0 && ', '}
              {upcomingEvents.length > 0 && `${upcomingEvents.length} upcoming`}
              {currentEvents.length === 0 && upcomingEvents.length === 0 && `${pastEvents.length} completed`}
            </span>
            <span>Last updated: {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      )}
    </div>
  )
}